<template>
  <v-card class="mt-4 mb-6 pt-1 points-measured-card">
    <v-row class="d-flex justify-center mt-2"
      ><h3
        :class="{
          'text-green': measuredPoints === activePoints,
        }"
      >
        {{
          $t("tests.illumination.measurement_card.title", [
            measuredPoints,
            activePoints,
          ])
        }}
      </h3>
    </v-row>

    <v-row>
      <v-col
        :class="
          state === 'in_evaluation' || state === 'completed' ? 'mb-0 pb-0' : ''
        "
        class="illuminance-column"
      >
        <h4>{{ $t("tests.illumination.measurement_card.illuminance") }}</h4>
        <v-row class="mx-0 my-3 d-flex justify-space-between align-baseline">
          <span class="caption black20">
            {{ $t("tests.illumination.measurement_card.required") }}
          </span>
          <span class="body2">
            {{ requiredAvgIlluminance }}
          </span>
        </v-row>

        <v-row
          class="mx-0 my-3 d-flex justify-space-between align-baseline"
          v-if="selectedTest.include_ra_rugl_maintenance"
        >
          <span class="caption black20">
            {{
              $t(
                "tests.illumination.measurement_card.required_minimum_with_maintenance_factor"
              )
            }}
          </span>
          <span class="body2">
            {{ requiredMinIlluminanceWithMaintenanceFactorText }}
          </span>
        </v-row>
        <v-row
          class="mx-0 my-3 d-flex justify-space-between align-baseline"
          v-else
        >
          <span class="caption black20">
            {{ $t("tests.illumination.measurement_card.required_minimum") }}
          </span>
          <span class="body2">{{ requiredMinIlluminanceText }}</span>
        </v-row>

        <v-row
          class="mx-0 my-3 d-flex justify-space-between align-baseline"
          v-if="state === 'in_evaluation' || state === 'completed'"
        >
          <span class="caption black20">
            {{ $t("tests.illumination.measurement_card.acceptable_deviation") }}
          </span>
          <span class="body2">{{ selectedTest.illuminance_deviation }}%</span>
        </v-row>

        <v-row class="mx-0 my-3 d-flex justify-space-between align-baseline">
          <span class="caption black20">
            {{ $t("tests.illumination.measurement_card.measured") }}
          </span>
          <span class="body2">{{ measuredAvgIlluminanceText }}</span>
        </v-row>

        <v-row
          class="mx-0 my-3 d-flex justify-space-between align-baseline"
          v-if="selectedTest.furniture === 'no'"
        >
          <span class="caption black20 furniture-label">
            {{ $t("tests.illumination.measurement_card.furniture") }}
          </span>
          <span class="body2">
            {{ measuredAvgIlluminanceDeductedFurnitureText }}
          </span>
        </v-row>

        <v-row
          class="
            mx-0
            mt-3
            pb-0
            mb-0
            d-flex
            justify-space-between
            align-baseline
          "
          v-if="state === 'in_evaluation' || state === 'completed'"
        >
          <span class="caption black20">
            {{
              $t("tests.illumination.measurement_card.measurement_deviation")
            }}
          </span>
          <span class="body2">{{ measuredAvgDeviation }}</span>
        </v-row>
      </v-col>

      <v-col class="mr-4 uniformity-column"
        ><h4>{{ $t("tests.illumination.measurement_card.uniformity") }}</h4>
        <v-row class="mx-0 my-3 d-flex justify-space-between align-baseline">
          <span class="caption black20">{{
            $t("tests.illumination.measurement_card.required")
          }}</span>
          <span class="body2">
            {{ selectedTest.uniformity.toFixed(2) }}
          </span>
        </v-row>

        <v-row class="mx-0 my-3 d-flex justify-space-between align-baseline">
          <span class="caption black20">{{
            $t("tests.illumination.measurement_card.measured")
          }}</span>
          <span class="body2">{{
            selectedTestResults.length > 0 ? measuredUniformity : "-"
          }}</span>
        </v-row>
      </v-col>

      <v-col
        v-if="state === 'in_evaluation' || state === 'completed'"
        class="deviation-column mt-n2 py-0"
        cols="12"
      >
        <v-row class="mx-0 my-3 d-flex justify-space-between align-baseline">
          <span class="caption text-grey-darker">{{
            $t("room.illumination.illuminance.deviation_info")
          }}</span>
        </v-row>
      </v-col>

      <v-col
        v-if="
          selectedTest.include_ra_rugl_maintenance &&
          (state === 'in_evaluation' || state === 'completed')
        "
        class="mr-4 ra-column"
      >
        <ra-card-info :test="selectedTest" />
      </v-col>
      <v-col
        v-if="
          selectedTest.include_ra_rugl_maintenance &&
          (state === 'in_evaluation' || state === 'completed')
        "
        class="mr-4 rugl-column"
      >
        <rugl-card-info :test="selectedTest" />
      </v-col>
      <v-col
        v-if="
          selectedTest.include_flicker &&
          (state === 'in_evaluation' || state === 'completed')
        "
        class="mr-4 flicker-percentage-column"
      >
        <flicker-percentage-card-info :test="selectedTest" />
      </v-col>
      <v-col
        v-if="
          selectedTest.include_flicker &&
          (state === 'in_evaluation' || state === 'completed')
        "
        class="mr-4 flicker-index-column"
      >
        <flicker-index-card-info :test="selectedTest" />
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import { meanBy } from "lodash";
import RaCardInfo from "./ra-rugl/RaCardInfo.vue";
import RuglCardInfo from "./ra-rugl/RuglCardInfo.vue";
import FlickerPercentageCardInfo from "./flicker/FlickerPercentageCardInfo.vue";
import FlickerIndexCardInfo from "./flicker/FlickerIndexCardInfo.vue";

export default {
  name: "points-measured-card",
  components: {
    RaCardInfo,
    RuglCardInfo,
    FlickerPercentageCardInfo,
    FlickerIndexCardInfo,
  },
  computed: {
    ...mapState("projectStore", ["state"]),
    ...mapState("roomStore", [
      "measurement_points",
      "illumination_tests",
      "selected_test_id",
    ]),
    measuredAvgDeviation() {
      const avg = this.measuredAvgIlluminance;
      const required = this.illumination_tests.find(
        (test) => test.illumination_test_id === this.selected_test_id
      ).average_illuminance;
      const measuredAvgDeviation = Math.round(
        (Math.abs(avg - required) / required) * 100
      );
      if (isNaN(measuredAvgDeviation)) {
        return "-";
      }
      return avg < required
        ? `-${measuredAvgDeviation}%`
        : `${measuredAvgDeviation}%`;
    },
    activePoints() {
      return this.measurement_points.filter((point) => point.is_active).length;
    },
    requiredAvgIlluminance() {
      const test = this.illumination_tests.find(
        (test) => test.illumination_test_id === this.selected_test_id
      );
      return `${test.average_illuminance} lux`;
    },
    requiredMinIlluminance() {
      const test = this.illumination_tests.find(
        (test) => test.illumination_test_id === this.selected_test_id
      );
      return (
        test.average_illuminance -
        (test.average_illuminance * test.illuminance_deviation) / 100
      );
    },
    requiredMinIlluminanceText() {
      return `${Math.round(this.requiredMinIlluminance)} lux`;
    },
    requiredMinIlluminanceWithMaintenanceFactor() {
      return this.requiredMinIlluminance / this.selectedTest.maintenance_factor;
    },
    requiredMinIlluminanceWithMaintenanceFactorText() {
      return `${Math.round(
        this.requiredMinIlluminanceWithMaintenanceFactor
      )} lux`;
    },
    selectedTest() {
      return this.illumination_tests.find(
        (test) => test.illumination_test_id === this.selected_test_id
      );
    },
    selectedTestResults() {
      return this.illumination_tests.find(
        (test) => test.illumination_test_id === this.selected_test_id
      ).illumination_test_results;
    },
    measuredAvgIlluminance() {
      return meanBy(
        this.selectedTestResults,
        (point) => point.measured_lux
      ).toFixed(2);
    },
    measuredAvgIlluminanceText() {
      return this.selectedTestResults.length > 0
        ? `${this.measuredAvgIlluminance} lux`
        : "-";
    },
    measuredAvgIlluminanceDeductedFurniture() {
      return (this.measuredAvgIlluminance * 0.9).toFixed(2);
    },
    measuredAvgIlluminanceDeductedFurnitureText() {
      return this.selectedTestResults.length > 0
        ? `${this.measuredAvgIlluminanceDeductedFurniture} lux`
        : "-";
    },
    measuredUniformity() {
      const minValue = Math.min(
        ...this.selectedTestResults.map((result) => {
          if (result.measured_lux !== null) {
            return result.measured_lux;
          }
        })
      );
      return (
        minValue /
        (this.measuredAvgIlluminance === 0 ? 1 : this.measuredAvgIlluminance)
      ).toFixed(2);
    },
    measuredPoints() {
      return this.illumination_tests.find(
        (test) => test.illumination_test_id === this.selected_test_id
      ).illumination_test_results.length;
    },
  },
};
</script>

<style lang="scss">
.points-measured-card {
  background-color: rgba(255, 255, 255, 0.1) !important;
  min-height: 157px !important;
  border-radius: 4px;
  padding: 8px 22px;
}
.furniture-label {
  white-space: pre-line;
}
@media only screen and (max-width: 1200px) {
  .illuminance-column {
    order: 1;
  }
  .uniformity-column {
    order: 3;
  }
  .deviation-column {
    order: 2;
  }
  .ra-column {
    order: 4;
  }
  .rugl-column {
    order: 5;
  }
  .flicker-percentage-column {
    order: 6;
  }
  .flicker-index-column {
    order: 7;
  }
}
</style>
